/**
 * Batch Allocation System for Nikolaus Nature
 * Automatically distributes orders across multiple batch variants when single batch inventory is insufficient
 *
 * Author: Med
 */

class BatchAllocation {
  constructor(config = {}) {
    this.config = {
      batchOptionNames: ["charge", "chargen"], // Configurable batch identifiers
      debug: false,
      enableInventoryValidation: true,
      fallbackOnError: true,
      ...config,
    };
    this.init();
  }

  init() {
    // Wait for theme to be ready
    if (window.MinimogTheme && window.MinimogSettings) {
      this.setupBatchAllocation();
    } else {
      document.addEventListener("DOMContentLoaded", () => {
        setTimeout(() => this.setupBatchAllocation(), 500);
      });
    }
  }

  log(...args) {
    if (this.config.debug) {
      console.log("[BatchAllocation]", ...args);
    }
  }

  setupBatchAllocation() {
    // Override the default add to cart functionality for products with batch variants
    this.interceptProductForms();
  }

  interceptProductForms() {
    // Use event delegation instead of method override for better reliability
    document.addEventListener(
      "submit",
      (event) => {
        const productForm = event.target.closest("product-form");
        if (productForm) {
          // Mark the form if not already marked
          if (!productForm.hasAttribute("data-batch-allocation")) {
            productForm.setAttribute("data-batch-allocation", "true");
          }
          // Always handle the submission for batch allocation check
          this.handleFormSubmission(event, productForm);
        }
      },
      true
    );

    // Mark existing forms
    const productForms = document.querySelectorAll(
      "product-form:not([data-batch-allocation])"
    );
    productForms.forEach((form) => {
      form.setAttribute("data-batch-allocation", "true");
    });
  }

  async handleFormSubmission(event, productFormElement) {
    // Check for form-specific processing to avoid race conditions
    if (productFormElement.dataset.processing === "true") {
      event.preventDefault();
      return;
    }

    const form = event.target;
    const formData = new FormData(form);
    const requestedQuantity = parseInt(formData.get("quantity") || "1");
    const selectedVariantId = parseInt(formData.get("id"));

    if (!selectedVariantId) {
      // No variant selected, proceed with original behavior
      return;
    }

    // Get product data
    const productId = productFormElement.dataset.productId;
    const productData = window._themeProducts?.[productId];

    if (!productData || !this.hasBatchVariants(productData)) {
      // No batch variants, proceed with original behavior
      console.error("No batch variants found");
      return;
    }

    // Check if allocation is needed
    const currentVariant = productData.variants.find(
      (v) => v.id === selectedVariantId
    );
    if (!currentVariant) {
      return;
    }

    const availableInventory = currentVariant.inventory_quantity || 0;

    if (requestedQuantity <= availableInventory && currentVariant.available) {
      // Sufficient inventory, proceed with original behavior
      return;
    }

    // Need batch allocation - prevent default and handle
    event.preventDefault();
    event.stopPropagation();

    productFormElement.dataset.processing = "true";

    try {
      const allocation = this.calculateBatchAllocation(
        productData,
        currentVariant,
        requestedQuantity
      );

      if (!allocation.success) {
        this.showError(allocation.message);
        return;
      }

      await this.executeBatchAllocation(
        allocation.batches,
        form,
        productFormElement
      );
    } catch (error) {
      console.error("Batch allocation error:", error);
      this.showError(
        "Ein Fehler ist beim Hinzufügen zum Warenkorb aufgetreten."
      );

      // Fallback: try original form submission
      try {
        productFormElement.dataset.processing = "false";
        const originalEvent = new Event("submit", {
          bubbles: true,
          cancelable: true,
        });
        form.dispatchEvent(originalEvent);
      } catch (fallbackError) {
        console.error("Fallback submission failed:", fallbackError);
      }
    } finally {
      productFormElement.dataset.processing = "false";
    }
  }

  hasBatchVariants(productData) {
    // Check if product has any of the configured batch option names
    this.log("Checking for batch variants in product:", productData);
    const hasBatch = productData.options?.some((option) =>
      this.config.batchOptionNames.some(
        (batchName) => option.name?.toLowerCase() === batchName.toLowerCase()
      )
    );
    this.log("Has batch variants:", hasBatch);
    return hasBatch;
  }

  calculateBatchAllocation(productData, currentVariant, requestedQuantity) {
    const chargeOptionIndex = this.getChargeOptionIndex(productData);

    if (chargeOptionIndex === -1) {
      return { success: false, message: "No charge option found" };
    }

    // Get compatible variants (same options except Charge)
    const compatibleVariants = productData.variants
      .filter((variant) => {
        return variant.options.every((option, index) => {
          if (index === chargeOptionIndex) return true; // Skip charge option
          return option === currentVariant.options[index];
        });
      })
      .filter((variant) => variant.available && variant.inventory_quantity > 0);

    if (compatibleVariants.length === 0) {
      return {
        success: false,
        message: "Nicht genügend Lagerbestand für diese Menge verfügbar.",
      };
    }

    // Sort by variant ID for consistent allocation
    compatibleVariants.sort((a, b) => a.id - b.id);

    // Calculate total available inventory
    const totalAvailable = compatibleVariants.reduce(
      (sum, variant) => sum + variant.inventory_quantity,
      0
    );

    if (totalAvailable < requestedQuantity) {
      return {
        success: false,
        message: `Nur ${totalAvailable} Stück verfügbar. Sie haben ${requestedQuantity} Stück angefordert.`,
      };
    }

    // Allocate quantities across batches
    const batches = [];
    let remainingQuantity = requestedQuantity;

    compatibleVariants.forEach((variant) => {
      if (remainingQuantity <= 0) return;

      const allocateQuantity = Math.min(
        remainingQuantity,
        variant.inventory_quantity
      );

      if (allocateQuantity > 0) {
        batches.push({
          variantId: variant.id,
          quantity: allocateQuantity,
          batchInfo: variant.options[chargeOptionIndex],
        });
        remainingQuantity -= allocateQuantity;
      }
    });

    return {
      success: true,
      batches: batches,
      totalAllocated: requestedQuantity - remainingQuantity,
    };
  }

  getChargeOptionIndex(productData) {
    const index =
      productData.options?.findIndex((option) =>
        this.config.batchOptionNames.some(
          (batchName) => option.name?.toLowerCase() === batchName.toLowerCase()
        )
      ) ?? -1;
    this.log("Charge option index:", index);
    return index;
  }

  async executeBatchAllocation(batches, form, productFormElement) {
    this.showLoadingState(form);

    try {
      // Validate inventory before execution to catch stale data
      const validationResult = await this.validateBatchInventory(batches);
      if (!validationResult.valid) {
        this.showError(validationResult.message);
        return;
      }

      // Prepare items for batch add to cart
      const items = batches.map((batch) => ({
        id: batch.variantId,
        quantity: batch.quantity,
      }));

      // Get cart sections if available
      const cart =
        productFormElement.cart || document.querySelector("m-cart-drawer");
      let requestData = { items };

      if (cart && cart.getSectionsToRender) {
        requestData.sections = cart
          .getSectionsToRender()
          .map((section) => section.id);
        requestData.sections_url = window.location.pathname;
      }

      const config = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        body: JSON.stringify(requestData),
      };

      const response = await fetch(
        window.MinimogSettings.routes.cart_add_url,
        config
      );
      const result = await response.json();

      if (result.status) {
        // Error occurred
        this.showError(result.message || result.description);
        return;
      }

      // Success - handle the same way as the original theme
      this.handleBatchSuccess(result, batches, cart, productFormElement);
    } catch (error) {
      console.error("Batch allocation error:", error);
      this.showError(
        "Ein Fehler ist beim Hinzufügen zum Warenkorb aufgetreten."
      );
    } finally {
      this.hideLoadingState(form);
    }
  }

  async validateBatchInventory(batches) {
    try {
      // Quick validation via cart.json to check current state
      const response = await fetch("/cart.json");
      if (!response.ok) {
        throw new Error("Network error");
      }

      // For now, return valid - in production you might want to:
      // 1. Fetch variant inventory via AJAX
      // 2. Compare with client-side data
      // 3. Recalculate allocation if needed
      return { valid: true };
    } catch (error) {
      console.warn("Inventory validation failed, proceeding:", error);
      return { valid: true }; // Fail open for better UX
    }
  }

  handleBatchSuccess(response, batches, cart, productFormElement) {
    const { MinimogSettings, MinimogStrings } = window;

    // Create success message
    const totalQuantity = batches.reduce(
      (sum, batch) => sum + batch.quantity,
      0
    );
    const batchCount = batches.length;
    const message =
      batchCount > 1
        ? `${totalQuantity} Artikel aus ${batchCount} Chargen zum Warenkorb hinzugefügt`
        : MinimogStrings.itemAdded;

    if (MinimogSettings.use_ajax_atc) {
      if (cart && MinimogSettings.enable_cart_drawer) {
        cart.renderContents(response);
        window.MinimogTheme.Notification.show({
          target: cart.querySelector("m-cart-drawer-items"),
          method: "prepend",
          type: "success",
          message: message,
          delay: 400,
        });
        window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
      } else {
        window.MinimogTheme.Notification.show({
          target: productFormElement.domNodes?.errorWrapper || document.body,
          method: "appendChild",
          type: "success",
          message: message,
          last: 3000,
          sticky: !productFormElement.domNodes?.errorWrapper,
        });

        this.updateCartCount();
      }

      // Emit theme events
      document.dispatchEvent(
        new CustomEvent("product-ajax:added", {
          detail: { product: response },
        })
      );
      window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, response);
    } else {
      window.location = MinimogSettings.routes.cart;
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch("/cart.json");
      const cart = await response.json();

      const cartCounts = document.querySelectorAll(".m-cart-count-bubble");
      cartCounts.forEach((cartCount) => {
        if (cart.item_count > 0) {
          cartCount.textContent = cart.item_count;
          cartCount.classList.remove("m:hidden");
        } else {
          cartCount.classList.add("m:hidden");
        }
      });
    } catch (error) {
      console.error("Error updating cart count:", error);
    }
  }

  showLoadingState(form) {
    const button = form.querySelector(".m-add-to-cart");
    if (button) {
      button.disabled = true;
      const spinner = button.querySelector(".m-spinner-icon");
      if (spinner) {
        spinner.style.display = "block";
      }
    }
  }

  hideLoadingState(form) {
    const button = form.querySelector(".m-add-to-cart");
    if (button) {
      button.disabled = false;
      const spinner = button.querySelector(".m-spinner-icon");
      if (spinner) {
        spinner.style.display = "none";
      }
    }
  }

  showError(message) {
    if (window.MinimogTheme && window.MinimogTheme.Notification) {
      window.MinimogTheme.Notification.show({
        target: document.body,
        method: "appendChild",
        type: "error",
        message: message,
        last: 5000,
        sticky: true,
      });
    } else {
      alert(message);
    }
  }
}

// Initialize the batch allocation system
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new BatchAllocation();
  });
} else {
  new BatchAllocation();
}
